/* eslint-disable react-hooks/exhaustive-deps */
import { Search, RotateCw, Copy } from 'lucide-react'
import toast, { Toaster } from 'react-hot-toast'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, Card, Col, Container, Form, Row, Table } from 'react-bootstrap'
import Select from 'react-select'
import Loader from '../../../components/atoms/Loader'
import RowEmpty from '../../../components/atoms/RowEmpty'
import TablePagination from '../../../components/atoms/Table/TablePagination'
import InputDatePicker from '../../../components/Input/DatePicker/InputDatePicker'
import api from '../../../config/AxiosInstance'
import { convertDateWithTime } from '../../../helpers/constants/constant.js'
import * as Common from '../../../utils/Common'
import { dateFormat } from '../../../utils/FormatDate'
import { firstLetterUpperCase } from '../../../utils/TextFormatter'
import { BadgeStatusPrLists } from '../../../components/atoms/StatusLabel'
import { useLocation, useNavigate } from 'react-router-dom'

const headerTitleStyle = {
  color: 'var(--primary-orange)',
  fontSize: '24px',
  fontWeight: '600'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardFilterStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const customStyles = {
  control: (base, state) => ({
    ...base,
    background: '#fff',
    borderColor: state.isFocused ? '#f89d1d' : '#e9ecef',
    borderRadius: '10px',
    boxShadow: state.isFocused
      ? '0 0 0 0.2rem rgba(248, 157, 29, 0.25)'
      : 'none',
    fontSize: '0.9rem',
    minHeight: '45px',
    '&:hover': {
      borderColor: '#f89d1d'
    }
  }),
  placeholder: (base) => ({
    ...base,
    color: '#adb5bd'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#f89d1d'
      : state.isFocused
        ? '#fff3cd'
        : null,
    color: state.isSelected ? 'white' : '#333',
    fontSize: '0.9rem',
    cursor: 'pointer',
    ':active': {
      ...base[':active'],
      backgroundColor: '#f89d1d'
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 8px'
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px'
  })
}

const TransferAreaPage = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const [warehouses, setWarehouses] = useState([])
  const [selectedWarehouse, setSelectedWarehouse] = useState(null)
  const [transferAreas, setTransferAreas] = useState([])
  const [dateFilter, setDateFilter] = useState({
    start_date: state?.start_date || null,
    end_date: state?.end_date || null
  })
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const fetchWarehouseRef = useRef(false)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [search])

  useEffect(() => {
    const fetchWarehouse = async () => {
      if (fetchWarehouseRef.current) return
      fetchWarehouseRef.current = true

      try {
        const params = {
          is_parent: 1,
          forUser: 1
        }
        const response = await api.get('/master/warehouses/all', { params })
        const { data } = response.data
        const options = data.map((w) => ({
          value: w.id,
          label: w.warehouse_name
        }))
        setWarehouses(options)

        if (state?.warehouse_id) {
          const found = options.find((w) => w.value === state.warehouse_id)
          if (found) setSelectedWarehouse(found)
        }
      } catch (err) {
        const errMsg =
          err?.response?.data?.errors?.message || 'An error occurred'
        Common.showPopUpErrorMessage(errMsg)
      }
    }
    fetchWarehouse()
  }, [])

  const fetchTransferArea = useCallback(async () => {
    if (!selectedWarehouse) {
      setTransferAreas([])
      setMeta((prev) => ({ ...prev, total_row: 0, total_page: 0 }))
      return
    }

    try {
      setLoading(true)

      const hasPartialDates =
        (dateFilter.start_date && !dateFilter.end_date) ||
        (!dateFilter.start_date && dateFilter.end_date)

      if (hasPartialDates) {
        setLoading(false)
        return
      }

      const hasBothDates = dateFilter.start_date && dateFilter.end_date

      const params = {
        per_page: meta.per_page,
        page: meta.current_page,
        warehouse_id: selectedWarehouse?.value || '',
        start_date: hasBothDates
          ? dateFormat(dateFilter.start_date, 'DD-MM-YYYY')
          : '',
        end_date: hasBothDates
          ? dateFormat(dateFilter.end_date, 'DD-MM-YYYY')
          : ''
      }

      if (debouncedSearch) params.q = debouncedSearch

      const response = await api.get('/transfer', {
        params
      })
      const { data, metadata } = response.data
      setTransferAreas(data)
      setMeta(metadata)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setLoading(false)
    }
  }, [
    meta.per_page,
    meta.current_page,
    selectedWarehouse,
    dateFilter,
    debouncedSearch
  ])

  const renderAction = (data) => (
    <div key={data.id}>
      <button
        className="btn btn-light text-secondary border rounded shadow-sm"
        style={{ width: '35px', height: '35px', padding: 0 }}
        onClick={() =>
          navigate(`/transfer/transfer-area/detail/${data.id}`, {
            state: {
              code: data.code,
              warehouse_id: selectedWarehouse?.value,
              start_date: dateFilter.start_date,
              end_date: dateFilter.end_date
            }
          })
        }
        title="View Detail"
      >
        <Search size={14} />
      </button>
    </div>
  )

  const columns = [
    {
      key: 'code',
      label: '#Ticket',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          {data.code ?? '-'}

          {data.code && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigator.clipboard.writeText(data.code)
                toast.success('Copied to clipboard')
              }}
              title="Copy"
            />
          )}
        </div>
      )
    },
    { key: 'created_at', label: 'Created' },
    {
      key: 'type',
      label: 'Type',
      component: (data) => firstLetterUpperCase(data.type)
    },
    { key: 'warehouse_id', label: 'Warehouse' },
    {
      key: 'status',
      label: 'Status',
      component: (data) =>
        BadgeStatusPrLists({ text: data.status.replaceAll('_', ' ') })
    },
    {
      key: 'code_erp',
      label: '#ERP DOC NO',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          {data.code_erp ?? '-'}

          {data.code_erp && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigator.clipboard.writeText(data.code_erp)
                toast.success('Copied to clipboard')
              }}
              title="Copy"
            />
          )}
        </div>
      )
    },
    {
      key: 'submited_at',
      label: 'Submitted',
      component: (data) =>
        data.submited_at ? convertDateWithTime(data.submited_at) : '-'
    },
    {
      key: 'action',
      label: 'Action',
      component: (data) => renderAction(data)
    }
  ]

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const handleReset = () => {
    setSelectedWarehouse(null)
    setDateFilter({ start_date: null, end_date: null })
    setSearch('')
    setDebouncedSearch('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  const handleChangeDateFilter = (type, value) => {
    let newDateFilter = {
      ...dateFilter,
      [type]: value
    }

    if (type === 'start_date' && value && dateFilter.end_date) {
      if (new Date(value) > new Date(dateFilter.end_date)) {
        newDateFilter.end_date = value
      }
    }

    if (type === 'end_date' && value && dateFilter.start_date) {
      if (new Date(value) < new Date(dateFilter.start_date)) {
        newDateFilter.start_date = value
      }
    }

    setDateFilter(newDateFilter)
    setMeta((prev) => ({ ...prev, current_page: 1 }))
  }

  useEffect(() => {
    fetchTransferArea()
  }, [fetchTransferArea])

  return (
    <Container fluid className="px-4 mb-5">
      <Toaster />
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Transfer Area</h3>
        </Col>
        <Col className="text-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={handleReset}
            style={{ fontSize: '0.85rem', height: '37px' }}
          >
            <RotateCw size={16} className="me-2" />
            Reset Filters
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search</Form.Label>
              <div
                className="d-flex align-items-center"
                style={{
                  height: '45px',
                  borderRadius: '10px',
                  border: '1px solid #e9ecef',
                  backgroundColor: '#ffffff',
                  fontSize: '0.9rem',
                  overflow: 'hidden'
                }}
              >
                <div
                  className="px-3 d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: '#f8f9fa',
                    borderRight: '1px solid #e9ecef',
                    height: '100%'
                  }}
                >
                  <Search size={16} className="text-muted" />
                </div>
                <Form.Control
                  type="text"
                  placeholder="Search by tiket or erp doc"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    border: 'none',
                    height: '100%',
                    paddingLeft: '1rem',
                    fontSize: '0.9rem'
                  }}
                  className="shadow-none"
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse</Form.Label>
              <Select
                options={warehouses}
                value={selectedWarehouse}
                onChange={(opt) => {
                  setSelectedWarehouse(opt)
                  setMeta((prev) => ({ ...prev, current_page: 1 }))
                }}
                placeholder="Select Warehouse"
                styles={customStyles}
                isClearable
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Start Date</Form.Label>
              <div
                style={FormControlStyle}
                className="d-flex align-items-center px-2"
              >
                <InputDatePicker
                  value={dateFilter.start_date}
                  onChange={(value) =>
                    handleChangeDateFilter('start_date', value)
                  }
                  placeholder="Start Date"
                  maxDate={
                    dateFilter.end_date ? new Date(dateFilter.end_date) : null
                  }
                  clearIcon
                />
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>End Date</Form.Label>
              <div
                style={FormControlStyle}
                className="d-flex align-items-center px-2"
              >
                <InputDatePicker
                  value={dateFilter.end_date}
                  onChange={(value) =>
                    handleChangeDateFilter('end_date', value)
                  }
                  placeholder="End Date"
                  minDate={
                    dateFilter.start_date
                      ? new Date(dateFilter.start_date)
                      : null
                  }
                  clearIcon
                />
              </div>
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row className="mt-4">
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              fontSize: '0.9rem',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                {columns.map((column) => (
                  <th
                    className="p-2 align-middle"
                    key={column.key}
                    style={tableHeaderStyle}
                  >
                    <span>{column.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columns.length} className="p-5 text-center">
                    <Loader />
                  </td>
                </tr>
              )}
              {!loading && transferAreas.length === 0 && (
                <RowEmpty colSpan={columns.length} />
              )}
              {!loading &&
                transferAreas.length > 0 &&
                transferAreas.map((transferArea, indexStock) => (
                  <tr
                    key={indexStock}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    {columns.map((column) => (
                      <td key={column.key} className="p-2 align-middle">
                        {column.component && column.component(transferArea)}
                        {!column.component && transferArea[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="p-3 border-top-0" colSpan={columns.length}>
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>
    </Container>
  )
}

export default TransferAreaPage
